import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Folder, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import AuthContext from '../../store/auth-context';
import PERMISSION_KEYS from '../../constants/permission-keys';
import {
    getFoldersForImplant,
    FolderForImplant,
} from '../../services/api/folderService';
import { FoldersForImplantDialog } from './FoldersForImplantDialog';

interface ImplantFoldersDisplayProps {
    implantId: string;
}

export const ImplantFoldersDisplay = ({
    implantId,
}: ImplantFoldersDisplayProps) => {
    const { t } = useTranslation();
    const ctx = useContext(AuthContext);
    const canManage = ctx.hasPermission(PERMISSION_KEYS.manageGroups);

    const [folders, setFolders] = useState<FolderForImplant[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    const fetchFolders = async () => {
        try {
            const result = await getFoldersForImplant(implantId);
            setFolders(result);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFolders();
    }, [implantId]);

    if (loading) return null;

    const memberFolders = folders.filter((f) => f.hasImplant);

    if (memberFolders.length === 0 && !canManage) return null;

    return (
        <>
            {memberFolders.length === 0 ? (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDialogOpen(true)}
                >
                    <Folder className="mr-2 h-4 w-4" aria-hidden="true" />
                    {t('folders.implantFolders.addToFolder')}
                </Button>
            ) : (
                <div className="flex flex-col gap-2">
                    <h4 className="flex items-center gap-2 text-sm font-medium">
                        {t('folders.implantFolders.linkedFolders')}
                        {canManage && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => setDialogOpen(true)}
                                aria-label={t(
                                    'folders.implantFolders.editAria'
                                )}
                            >
                                <Pencil
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                            </Button>
                        )}
                    </h4>
                    {memberFolders.map((folder) => (
                        <Link
                            key={folder._id}
                            to={`/group/${folder._id}`}
                            title={
                                folder.parentTitle
                                    ? `${folder.parentTitle} › ${folder.title}`
                                    : folder.title
                            }
                            className="flex items-start gap-2 text-sm text-foreground"
                        >
                            <Folder
                                className="mt-[3px] h-3.5 w-3.5 shrink-0"
                                aria-hidden="true"
                            />
                            <span className="min-w-0 break-words">
                                {folder.title}
                            </span>
                        </Link>
                    ))}
                </div>
            )}

            <FoldersForImplantDialog
                implantId={implantId}
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                onChanged={fetchFolders}
            />
        </>
    );
};
